import React, { useState } from "react";

import { useAssumableRoles } from "src/adminPanel/queries";
import { compareRoleNames } from "src/adminPanel/shared/roleCatalog";
import { AssumableRoleRow } from "src/adminPanel/users/AssumableRoleRow";
import { resolveRoleReach } from "src/adminPanel/users/assumableRoleUtils";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { AssumableRole, OrganizationInDB } from "src/clients/taktile-api";
import { Accordion } from "src/design-system/Accordion";
import { Button } from "src/design-system/Button";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { ErrorContent } from "src/router/ErrorPage";
import {
  useTokenFlowFolderScope,
  useTokenRoleScope,
} from "src/store/AuthStore";
import { useSetRoleScopeMutation } from "src/store/authQueries";

const GROUPS = [
  {
    key: "held",
    title: "Roles you hold",
    description: "Granted to you directly.",
    matches: (role: AssumableRole) => role.held_directly,
  },
  {
    key: "derived",
    title: "Roles you can step down to",
    description:
      "Not granted to you directly, but contained in a role you hold — a workspace you can edit is a workspace you can view.",
    matches: (role: AssumableRole) => !role.held_directly,
  },
] as const;

const FLOW_FOLDER_ARG = "flow_folder_id";

export const TokenImpersonationModal: React.FC<{
  isOpen: boolean;
  organizations: OrganizationInDB[];
  workspaces: WorkspaceDataplane[];
  onClose: () => void;
}> = ({ isOpen, organizations, workspaces, onClose }) => {
  const activeScope = useTokenRoleScope();
  const activeFolder = useTokenFlowFolderScope();
  const { mutate: setRoleScope, isPending: isMinting } =
    useSetRoleScopeMutation();
  // Only fetched while the modal is open: the walk is cheap but it is a role
  // graph, not something the users tab needs before you ask for it.
  const assumableRoles = useAssumableRoles(isOpen);

  const [selected, setSelected] = useState<string[]>(activeScope ?? []);
  const [flowFolderId, setFlowFolderId] = useState(activeFolder ?? "");
  const [wasOpen, setWasOpen] = useState(isOpen);

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setSelected(activeScope ?? []);
      setFlowFolderId(activeFolder ?? "");
    }
  }

  const takesFlowFolder = (assumableRoles.data ?? []).some(
    (role) =>
      selected.includes(role.name) && role.arg_names.includes(FLOW_FOLDER_ARG),
  );

  const handleToggle = (name: string) =>
    setSelected((current) =>
      current.includes(name)
        ? current.filter((role) => role !== name)
        : [...current, name],
    );

  const handleMint = () =>
    setRoleScope(
      {
        roles: selected,
        flowFolderId: takesFlowFolder
          ? flowFolderId.trim() || undefined
          : undefined,
      },
      {
        onError: () =>
          toastActions.failure({
            title: "Could not mint the token",
            description:
              "The impersonation was rejected. Your session is unchanged.",
          }),
      },
    );

  return (
    <Modal open={isOpen} size="md" onClose={onClose}>
      <Modal.Header description="Mint a session token that carries only the roles you select. The whole application — UI and API alike — sees just those roles until you remove the impersonation or log out.">
        Token impersonation
      </Modal.Header>
      <Modal.Content>
        {assumableRoles.isPending ? (
          <Skeleton height="h-5" rounded="rounded-lg" />
        ) : assumableRoles.isError ? (
          <ErrorContent
            message={assumableRoles.error?.message ?? "Please try again later."}
          />
        ) : (
          <div className="flex flex-col gap-4">
            {GROUPS.map((group) => {
              const inGroup = assumableRoles
                .data!.filter(group.matches)
                .sort((a, b) => compareRoleNames(a.name, b.name));
              return inGroup.length === 0 ? null : (
                <div key={group.key}>
                  <p className="font-inter-medium-12px text-gray-800">
                    {group.title}
                  </p>
                  <p className="mb-1.5 font-inter-normal-12px text-gray-500">
                    {group.description}
                  </p>
                  <Accordion dataLoc={`token-impersonation-roles-${group.key}`}>
                    {inGroup.map((role) => (
                      <AssumableRoleRow
                        key={role.name}
                        isDisabled={isMinting}
                        isSelected={selected.includes(role.name)}
                        reach={resolveRoleReach(
                          role,
                          workspaces,
                          organizations,
                        )}
                        role={role}
                        onToggle={() => handleToggle(role.name)}
                      />
                    ))}
                  </Accordion>
                </div>
              );
            })}
            {takesFlowFolder && (
              <div>
                <p className="font-inter-medium-12px text-gray-800">
                  Flow folder
                </p>
                <p className="mb-1.5 font-inter-normal-12px text-gray-500">
                  Restricts the selected flow roles to one folder. Leave empty
                  to keep every folder they already reach.
                </p>
                <Input
                  dataLoc="token-impersonation-flow-folder"
                  disabled={isMinting}
                  placeholder="Flow folder ID"
                  value={flowFolderId}
                  variant="monospaced"
                  onChange={(event) => setFlowFolderId(event.target.value)}
                />
              </div>
            )}
            <p className="font-inter-normal-12px text-gray-500">
              {selected.length} of {assumableRoles.data!.length} roles selected
            </p>
          </div>
        )}
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="token-impersonation-mint"
            isDisabled={selected.length === 0 || isMinting}
            isLoading={isMinting}
            onClick={handleMint}
          >
            Mint token
          </Button>
        }
      />
    </Modal>
  );
};
